import { useMemo } from 'react';
import { format } from 'date-fns';
import styled from 'styled-components';

import MoreContentRequester from 'components/MoreContentRequester';
import Button from 'components/shared/Button';
import StandardModal from 'components/shared/StandardModal';
import { devices } from 'styles/theme/devices';

import useDischargeCalendarDayInfinite from '../hooks/useDischargeCalendarDayInfinite';
import DischargeCalendarPatientCard from '../shared/DischargeCalendarPatientCard';
import { DayViewColumnSkeleton } from '../skeletons';
import { getPayerType } from '../utils/patientUtils';

interface DayPatientsModalProps {
  date: Date;
  totalCount?: number;
  onClose: () => void;
}

export default function DayPatientsModal({ date, totalCount, onClose }: DayPatientsModalProps) {
  const formattedDate = format(date, 'EEEE, MMMM do');

  const infiniteQuery = useDischargeCalendarDayInfinite({
    date,
  });

  const patientsForDay = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.data) ?? [],
    [infiniteQuery.data]
  );

  return (
    <StandardModal title={formattedDate} onCancel={onClose} closeOnBackdropClick>
      <ModalBody>
        <PatientCount data-cy='day-patients-modal-count'>
          {totalCount} Projected Discharge{totalCount !== 1 ? 's' : ''}
        </PatientCount>
        <PatientList data-cy='day-patients-modal-list'>
          {infiniteQuery.isLoading ? (
            <DayViewColumnSkeleton count={Math.min(totalCount ?? 5, 25)} />
          ) : (
            <>
              {patientsForDay.map((entry, i) => (
                <DischargeCalendarPatientCard key={i} entry={entry} payerType={getPayerType(entry)} />
              ))}
              <MoreContentRequester resource={infiniteQuery} />
            </>
          )}
        </PatientList>
        <ButtonContainer>
          <Button variant='primary' onClick={onClose} data-cy='day-patients-modal-close'>
            Close
          </Button>
        </ButtonContainer>
      </ModalBody>
    </StandardModal>
  );
}

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const PatientCount = styled.div`
  font-size: 14px;
  font-weight: var(--font-weight-bold);
  color: ${({ theme }) => theme.colors.black};
  text-align: center;
  margin-bottom: 16px;
`;

const PatientList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  overflow-y: auto;
  max-height: 400px;

  @media ${devices.desktop} {
    max-height: 50vh;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
`;
